/* eslint-disable max-lines */
import styled from '@emotion/styled';
import React from 'react';
import Typography from 'aquilla/core/atoms/Typography';
import Button from 'aquilla/core/molecules/Button';
import { mediaQuery, Spacings, Fonts } from '@styles/index';
import { useRouter } from 'next/router';
import pushRoute from '@utils/pushRoute';
import {
  LEARNING_FOR_FREE_CLICKED,
  SUBSCRIPTION_PLAN_CLICKED
} from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { segment } from '@actions/analytics';
import Discount from 'aquilla/icons/subscriptions/Discount';
import {
  entityContentType,
  sideNavFlags as sideNavFlagsConst
} from '@constants/index';
import {
  getIsCatalogueFeatureAvailable,
  getIsNoTestGoal,
  getIsPracticeAvailable,
  getIsPrimeEnabled
} from '@api/selectors/topology';
import useTopology from '@api/hooks/topology/useTopology';
import useConstants from '@api/hooks/useConstants';
import { getIsGTPGoal } from '@api/selectors/constants';
import useSidenavFlags from '@api/hooks/useSidenavFlags';
import Link from 'aquilla/core/molecules/Link';
import deviceHelper from '@utils/deviceHelper';
import { generateFeatureCards } from './utils';

const SubscriptionTopContainer = styled.div`
  width: var(--layout-width);
  margin: 0 auto ${Spacings.SPACING_12B};

  ${mediaQuery.uptoTablet} {
    width: 100%;
    margin: 0 auto ${Spacings.SPACING_8B};
    padding: 0 ${Spacings.SPACING_8B};
  }
  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin: 0 auto ${Spacings.SPACING_8B};
    padding: 0 ${Spacings.SPACING_4B};
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};
  margin-top: ${Spacings.SPACING_8B};
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const MwebContainer = styled.div`
  display: none;
  ${mediaQuery.uptoMobile} {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: ${Spacings.SPACING_3B};
    margin-top: ${Spacings.SPACING_6B};
    padding: ${Spacings.SPACING_2B} 0 ${Spacings.SPACING_4B};
  }
`;

const DESKTOP_CARDS_PER_ROW = 4;

const getDesktopFeatureCardRows = (cardCount) => {
  const rows = [];
  for (let i = 0; i < cardCount; i += DESKTOP_CARDS_PER_ROW) {
    const row = [];
    const end = Math.min(i + DESKTOP_CARDS_PER_ROW, cardCount);
    for (let j = i; j < end; j += 1) {
      row.push(j);
    }
    rows.push(row);
  }
  return rows;
};

const CardRow = styled.div`
  display: ${({ $useThreeColGrid }) => ($useThreeColGrid ? 'grid' : 'flex')};
  grid-template-columns: repeat(3, 1fr);
  gap: ${Spacings.SPACING_4B};
  width: 100%;
`;

const CardContainer = styled.div`
  display: flex;
  position: relative;
  max-width: 272px;
  min-width: 272px;
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B} ${Spacings.SPACING_4B}
    ${Spacings.SPACING_3B};
  flex-direction: column;
  align-items: flex-start;
  gap: ${Spacings.SPACING_4B};
  border-radius: ${Spacings.SPACING_2B};
  border: 1px solid var(--color-divider);
  transition: border-color 0.3s;

  &:hover {
    border-color: ${(props) => props.color || 'black'};
  }
  ${mediaQuery.uptoTablet} {
    min-width: 162px;
    padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_3B}
      ${Spacings.SPACING_2B} ${Spacings.SPACING_2B};
    :last-of-type {
      grid-column: ${(props) => (props.isOddElements ? '1 / span 2' : '')};
      justify-self: ${(props) => (props.isOddElements ? 'center' : '')};
    }
  }
`;

const PrimeCardContainer = styled(CardContainer)`
  overflow: hidden;
  border: 1px solid var(--color-i-violet-20);

  background-image: url('/static/images/prime_background.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  &:hover {
    border-color: var(--color-i-violet-14);
  }
`;

const PrimeArtwork = styled.img`
  position: absolute;
  right: 0;
  bottom: 0;
  height: 56px;
  width: auto;
  pointer-events: none;
  user-select: none;
`;

const PrimeBadge = styled.img`
  height: 16px;
  width: auto;
  display: inline-block;
  vertical-align: middle;
  flex-shrink: 0;
`;

const PrimeTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacings.SPACING_2B};
`;

const PrimeSubtitle = styled(Typography)`
  position: relative;
  max-lines: 2;
  padding-right: ${Spacings.SPACING_10B};
  text-overflow: ellipsis;
  z-index: 1;
`;

const CardInfo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${Spacings.SPACING_2B};
  align-self: stretch;
  rect {
    width: 0px !important;
  }
`;

const InfoContainer = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  gap: ${Spacings.SPACING_1B};
  align-items: flex-start;
`;

const StyledLink = styled(Link)`
  align-items: baseline;
  svg {
    width: ${Spacings.SPACING_6B};
  }
  & .anchor-arrow {
    position: absolute;
    right: ${Spacings.SPACING_4B};
    top: ${Spacings.SPACING_4B};
  }
  ${mediaQuery.uptoMobile} {
    svg {
      width: ${Spacings.SPACING_5B};
      align-self: center;
    }
    & .anchor-arrow {
      position: absolute;
      right: ${Spacings.SPACING_3B};
      top: ${Spacings.SPACING_3B};
    }
  }
`;

const Header = styled.div`
  margin-bottom: 12px;
  width: 100%;

  ${mediaQuery.uptoTablet} {
    width: 90%;
  }
`;

const WebHeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: ${Spacings.SPACING_8B};
`;

const ButtonsWrapper = styled.div`
  display: none;
  ${mediaQuery.uptoMobile} {
    display: flex;
    flex-direction: column;
    grid-gap: ${Spacings.SPACING_4B};
    margin-top: ${Spacings.SPACING_6B};
  }
`;

const WebButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  align-self: flex-start;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: ${Spacings.SPACING_6B};
`;

const ViewSubscriptionOfferContainer = styled.div`
  display: flex;
  margin-top: 16px;
  width: 218px;
  gap: ${Spacings.SPACING_1B};
  justify-content: center;
  .label {
    cursor: pointer;
    border-bottom: 1.5px dashed;
  }

  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin-top: ${Spacings.SPACING_4B};
  }
`;

const DiscountIcon = styled(Discount)`
  color: var(--color-i-yellow-10);
`;

const SubHeaderWrapper = styled.div`
  font-size: ${Fonts.LARGE};
  font-weight: ${Fonts.SEMIBOLD};
  line-height: ${Fonts.HEADER_LINE_HEIGHT};
  letter-spacing: -0.005em;
  color: var(--color-text-primary);
`;

const MwebImageWrapper = styled.div`
  display: none;
  ${mediaQuery.uptoMobile} {
    display: flex;
    justify-content: center;
  }
`;

const SubHeaderText = styled.span``;

const MwebHeaderTitle = styled(Typography)`
  display: none;
  ${mediaQuery.uptoMobile} {
    display: block;
  }
`;

const WebHeaderTitle = styled(Typography)`
  display: block;
  font-size: clamp(32px, 3em, 48px);
  line-height: clamp(40px, 1.2em, 56px);
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const StyledButton = styled(Button)`
  min-width: 220px;
  white-space: nowrap;
`;

const StyledTypography = styled(Typography)`
  ${mediaQuery.uptoMobile} {
    font-weight: 600;
  }
`;

const isAADEnabled = (array = []) => {
  const ele = array.find(
    (feature) => feature.type === sideNavFlagsConst.DOUBTS_AND_SOLUTIONS
  );
  const ele2 = ele?.isAskADoubtEnabled;
  return !!ele2;
};

const SubscriptionTop = ({ block }) => {
  const {
    query: { goalSlug, goalUID }
  } = useRouter();
  const isMobile = deviceHelper.isMobileDevice();
  const goalSegmentData = useGoalEventProps({ goalUID });
  const { topology: goalInfo } = useTopology(goalUID);
  const isGTP = useConstants(getIsGTPGoal(goalUID));
  const isPracticeAvailable = getIsPracticeAvailable(goalInfo) || isGTP;
  const isNoTestGoal = getIsNoTestGoal(goalInfo);
  const { sidenavFlags } = useSidenavFlags(goalUID);
  const { features } = sidenavFlags || { features: [] };
  const isAADEnabledFlag = isAADEnabled(features);
  const isCatalogueFeatureAvailable = getIsCatalogueFeatureAvailable(goalInfo);
  const isPrimeEnabledFlag = getIsPrimeEnabled(goalInfo);

  const featureCardsMapping = generateFeatureCards(
    isPracticeAvailable,
    isNoTestGoal,
    isAADEnabledFlag,
    isCatalogueFeatureAvailable,
    isPrimeEnabledFlag,
    goalSlug,
    goalUID
  );

  const { header, showSubscriptionPlanLabel, showOffersTouchpoint } =
    block.extraBlockInfo;
  const handleClick = (e, cardData) => {
    e.preventDefault();
    segment.track(cardData.eventName, {
      last_primary_source: 'Free Home Page',
      last_primary_source_section: 'Feature cards',
      ...goalSegmentData
    });
    const route = {
      href: cardData.href,
      as: cardData.as
    };
    pushRoute(route.href, route.as);
  };

  const CardGrid = ({ cardCount }) => {
    const isOddElements = cardCount % 2 !== 0;
    const desktopRows = getDesktopFeatureCardRows(cardCount);
    if (isMobile)
      return (
        <MwebContainer isOddElements={isOddElements}>
          {[...Array(cardCount)].map((_, index) => (
            <Card
              key={featureCardsMapping[index]?.title}
              index={index}
              isOddElements={isOddElements}
            >
              Card {index + 1}
            </Card>
          ))}
        </MwebContainer>
      );
    return (
      <Container>
        {desktopRows.map((rowIndices, rowIdx) => (
          <CardRow
            key={rowIndices[0]}
            $useThreeColGrid={cardCount === 6 && rowIdx === 0}
          >
            {rowIndices.map((index) => (
              <Card key={featureCardsMapping[index]?.title} index={index}>
                Card {index + 1}
              </Card>
            ))}
          </CardRow>
        ))}
      </Container>
    );
  };

  const TileIcon = ({ height, width, Component }) => {
    return <Component height={height} width={width} />;
  };

  const PrimeHeroCard = ({ cardData, isOddElements }) => {
    return (
      <PrimeCardContainer
        key={cardData?.title}
        color={cardData?.color}
        isOddElements={isOddElements}
      >
        <StyledLink underline={false} onClick={(e) => handleClick(e, cardData)}>
          <CardInfo>
            <TileIcon
              height={isMobile ? 20 : 32}
              width={isMobile ? 20 : 32}
              Component={cardData.icon}
            />
            <InfoContainer>
              <PrimeTitleRow>
                <StyledTypography variant={isMobile ? 'p3' : 'h5'}>
                  {isMobile ? cardData.mwebTitle : cardData?.title}
                </StyledTypography>
                {cardData?.badgeIconSrc && (
                  <PrimeBadge src={cardData.badgeIconSrc} alt="New" />
                )}
              </PrimeTitleRow>
              {!isMobile && (
                <PrimeSubtitle variant="p3" color="var(--light-base-fill)">
                  {cardData?.subtitle}
                </PrimeSubtitle>
              )}
            </InfoContainer>
          </CardInfo>
        </StyledLink>
        {cardData?.artworkSrc && (
          <PrimeArtwork src={cardData.artworkSrc} alt="" aria-hidden="true" />
        )}
      </PrimeCardContainer>
    );
  };

  const Card = ({ index, isOddElements }) => {
    const cardData = featureCardsMapping[index];
    if (cardData?.variant === 'hero') {
      return (
        <PrimeHeroCard cardData={cardData} isOddElements={isOddElements} />
      );
    }
    return (
      <CardContainer
        key={cardData?.title}
        index={index}
        color={cardData?.color}
        isOddElements={isOddElements}
      >
        <StyledLink
          rightArrow
          underline={false}
          onClick={(e) => handleClick(e, cardData)}
        >
          <CardInfo>
            <TileIcon
              height={isMobile ? 20 : 32}
              width={isMobile ? 20 : 32}
              Component={cardData.icon}
            />
            <InfoContainer>
              <StyledTypography variant={isMobile ? 'p3' : 'h5'}>
                {isMobile ? cardData.mwebTitle : cardData?.title}
              </StyledTypography>
              {!isMobile && (
                <Typography variant="p3" color="var(--light-base-fill)">
                  {cardData?.subtitle}
                </Typography>
              )}
            </InfoContainer>
          </CardInfo>
        </StyledLink>
      </CardContainer>
    );
  };

  const getHeader = () => {
    return (
      <Header>
        <MwebHeaderTitle variant="h2">{header}</MwebHeaderTitle>
        <WebHeaderTitle variant="h1">{header}</WebHeaderTitle>
      </Header>
    );
  };

  const SubHeader = ({ subHeaderValues }) => {
    if (Array.isArray(subHeaderValues))
      return subHeaderValues.map((subHeaderValue, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <SubHeader key={i} subHeaderValues={subHeaderValue} />
      ));
    return (
      <SubHeaderText style={subHeaderValues.style || {}}>
        {' '}
        {subHeaderValues.content}{' '}
      </SubHeaderText>
    );
  };
  const getSubHeader = () => {
    const { subHeader, subHeaderValues } = block.extraBlockInfo;
    if (subHeaderValues)
      return (
        <SubHeaderWrapper>
          <SubHeader subHeaderValues={subHeaderValues} />
        </SubHeaderWrapper>
      );

    return <SubHeaderWrapper dangerouslySetInnerHTML={{ __html: subHeader }} />;
  };

  const ViewSubscriptionOffer = () => {
    const href = `/goal/${goalSlug}/${goalUID}/offers`;
    return (
      <ViewSubscriptionOfferContainer>
        <DiscountIcon width={24} height={24} color="var(--color-i-yellow-10)" />
        <Link href={href} underline={false}>
          <Typography
            className="label"
            variant="h6"
            color="var(--color-i-yellow-10)"
          >
            View subscription offers
          </Typography>
        </Link>
      </ViewSubscriptionOfferContainer>
    );
  };
  const handleTryFree = (e) => {
    e.preventDefault();
    segment.track(LEARNING_FOR_FREE_CLICKED, {
      ...goalSegmentData
    });
    const route = {
      href: '/goal/[goalSlug]/[goalUID]/browse',
      as: `/goal/${goalSlug}/${goalUID}/browse`
    };
    pushRoute(route.href, route.as);
  };

  const handleSubscription = (e) => {
    e.preventDefault();
    segment.track(SUBSCRIPTION_PLAN_CLICKED, {
      ...goalSegmentData,
      entity_content_type: entityContentType.TOPOLOGY
    });
    const route = {
      href: '/goal/[goalSlug]/[goalUID]/subscriptions',
      as: `/goal/${goalSlug}/${goalUID}/subscriptions`
    };
    pushRoute(route.href, route.as);
  };

  if (!block) return null;
  return (
    <SubscriptionTopContainer>
      <WebHeaderWrapper>
        <ContentWrapper>
          {getHeader()}
          {getSubHeader()}
        </ContentWrapper>
        <WebButtonWrapper>
          {showSubscriptionPlanLabel && (
            <ButtonWrapper>
              <StyledButton
                onClick={handleTryFree}
                label="Try learning for free"
                variant="secondary"
                size="large"
              />
              <StyledButton
                onClick={handleSubscription}
                label="View subscription plans"
                variant="brand"
                size="large"
              />
            </ButtonWrapper>
          )}
          {showOffersTouchpoint && <ViewSubscriptionOffer />}
        </WebButtonWrapper>
      </WebHeaderWrapper>
      {isMobile && <CardGrid cardCount={featureCardsMapping.length} />}
      <ButtonsWrapper>
        {showSubscriptionPlanLabel && (
          <Button
            onClick={handleSubscription}
            label="View subscription plans"
            variant="brand"
            size="large"
          />
        )}
        <Button
          onClick={handleTryFree}
          label="Explore for free"
          variant="secondary"
          size="large"
        />
      </ButtonsWrapper>
      <MwebImageWrapper>
        {showOffersTouchpoint && <ViewSubscriptionOffer />}
      </MwebImageWrapper>
      {!isMobile && <CardGrid cardCount={featureCardsMapping.length} />}
    </SubscriptionTopContainer>
  );
};

export default SubscriptionTop;
